import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Link2,
  Pencil,
  PlusCircle,
  RefreshCw,
  RotateCcw,
  Tag,
  Trash2,
  Upload,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

import { api, formatApiError, fmtDate, wibDaysAgo } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { ShippingVariantDetails } from "../components/finance/ShippingVariantDetails";

const STATUS_META = {
  full_match: { label: "Cocok Lengkap", className: "bg-status-passed/15 text-status-passed border-0" },
  needs_variant_check: { label: "Perlu Cek Varian", className: "bg-amber-100 text-amber-800 border-0" },
  variant_mismatch: { label: "Beda Produk/Varian", className: "bg-red-100 text-red-700 border-0" },
  qty_mismatch: { label: "Beda Jumlah", className: "bg-red-100 text-red-700 border-0" },
  cashier_only: { label: "Kasir tanpa File", className: "bg-red-100 text-red-700 border-0" },
  marketplace_only: { label: "Marketplace tanpa Kasir", className: "bg-amber-100 text-amber-800 border-0" },
  duplicate_cashier: { label: "Sales Ganda", className: "bg-red-100 text-red-700 border-0" },
  multiple_packages: { label: "Paket / Resi Ganda", className: "bg-amber-100 text-amber-800 border-0" },
  verified_exception: { label: "Pengecualian Terverifikasi", className: "bg-status-passed/15 text-status-passed border-0" },
  matched: { label: "Cocok Lengkap", className: "bg-status-passed/15 text-status-passed border-0" },
};

const resultOf = (row) => row.result_status || row.match_status;
const isDone = (row) => ["full_match", "verified_exception"].includes(resultOf(row));
const isReviewed = (row) => isDone(row) || ["handled", "linked", "resolved"].includes(row.review_status);

const ACTION_META = {
  resolved: "Selesai Ditinjau",
  retry_shipping: "Kirim Ulang",
  recommend_cancel: "Rekomendasi Batal",
  outside_marketplace: "Penjualan Luar Marketplace",
  link_flash_order: "Pesanan Kilat Terhubung",
  mark_flash_order: "Tandai Pesanan Kilat",
};

const REASON_META = {
  flash_order: "Pesanan Kilat · Stok di Shopee",
  missing_shipping_file: "Kasir di luar file pengiriman",
  customer_from_flash_order: "Order customer dari Pesanan Kilat",
};

const emptyDetail = { batch: null, rows: [] };

function SummaryCard({ label, value, tone = "text-cocoa" }) {
  return (
    <Panel className="p-4">
      <div className="text-xs uppercase tracking-wide text-wmuted">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${tone}`} data-testid={`shipping-summary-${label}`}>
        {value || 0}
      </div>
    </Panel>
  );
}

export default function MarketplaceShippingReconciliation() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const canWrite = can("accounting", "warehouse_admin", "sales");
  const [start, setStart] = useState(wibDaysAgo(1));
  const [end, setEnd] = useState(wibDaysAgo(1));
  const [marketplace, setMarketplace] = useState("shopee");
  const [file, setFile] = useState(null);
  const [storeMappings, setStoreMappings] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [batches, setBatches] = useState([]);
  const [detail, setDetail] = useState(emptyDetail);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [review, setReview] = useState(null);
  const [flashLink, setFlashLink] = useState(null);
  const [flashOnly, setFlashOnly] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const loadBatches = async () => {
    setLoading(true);
    try {
      const response = await api.get("/marketplace-shipping/batches");
      setBatches(response.data);
      if (!detail.batch && response.data[0]) {
        await openBatch(response.data[0].id);
      }
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBatches();
  }, []);

  useEffect(() => {
    api.get("/marketplace-shipping/store-mappings")
      .then((res) => setStoreMappings(res.data.mappings || []))
      .catch(() => {});
  }, []);

  const platformStores = storeMappings.filter((s) => s.platform === marketplace);

  const openBatch = async (batchId) => {
    setPage(1);
    if (!batchId) {
      setDetail(emptyDetail);
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/marketplace-shipping/batches/${batchId}`);
      setDetail(response.data);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const upload = async () => {
    if (!file) {
      toast.error("Pilih file shipping Shopee terlebih dahulu");
      return;
    }
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("File shipping harus berformat XLSX");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("marketplace", marketplace);
      form.append("start", start);
      form.append("end", end);
      form.append("store_id", storeId);
      const response = await api.post("/marketplace-shipping/upload", form);
      toast.success("Crosscheck pengiriman selesai dibuat");
      setFile(null);
      await loadBatches();
      await openBatch(response.data.id);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const exportBatch = async () => {
    if (!detail.batch) {
      return;
    }
    try {
      const response = await api.get(
        `/marketplace-shipping/batches/${detail.batch.id}/export`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `crosscheck-pengiriman-${detail.batch.start}.xlsx`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const recheckBatch = async () => {
    if (!detail.batch) {
      return;
    }
    setLoading(true);
    try {
      const response = await api.post(`/marketplace-shipping/batches/${detail.batch.id}/recheck`);
      setDetail(response.data);
      toast.success("Periksa ulang selesai — dicocokkan ke Sale List terkini");
      await loadBatches();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const deleteBatch = async () => {
    if (!detail.batch) return;
    if (!window.confirm(`Hapus batch crosscheck ini?\n${detail.batch.filename}\n(${detail.batch.start} → ${detail.batch.end})\n\nSeluruh baris crosscheck batch ini dihapus & status rekonsiliasi Sales terkait direset. Setelah ini file yang sama bisa di-upload ulang. Data Sales/kasir TIDAK ikut terhapus.`)) return;
    setLoading(true);
    try {
      await api.delete(`/marketplace-shipping/batches/${detail.batch.id}`);
      toast.success("Batch crosscheck dihapus. Silakan upload ulang file-nya.");
      setDetail(emptyDetail);
      await loadBatches();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const createCashierFromOrder = async (row) => {
    if (!detail.batch) {
      return;
    }
    try {
      const res = await api.get(
        `/marketplace-shipping/batches/${detail.batch.id}/rows/${row.id}/cashier-draft`,
      );
      if (!res.data.ok) {
        const count = res.data.unmapped?.length || 0;
        toast.error(`${count} item belum di-mapping ke varian ERP. Lengkapi Mapping Varian dulu.`);
        navigate("/finance/marketplace-mapping");
        return;
      }
      navigate("/sales/create", { state: { draft: res.data.draft } });
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const deleteSale = async (row, sale) => {
    if (!window.confirm(`Hapus Sales ${sale.number}? Stok akan dikembalikan ke Available dan data Sales dihapus permanen.`)) {
      return;
    }
    try {
      await api.delete(`/marketplace-shipping/batches/${detail.batch.id}/rows/${row.id}/sales/${sale.id}`);
      toast.success(`Sales ${sale.number} dihapus`);
      await openBatch(detail.batch.id);
      await loadBatches();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const visibleRows = useMemo(() => detail.rows.filter((row) => {
    if (
      flashOnly
      && (row.match_status !== "cashier_only" || row.reason_code !== "flash_order")
    ) {
      return false;
    }
    if (statusFilter === "needs_review" && isReviewed(row)) {
      return false;
    }
    if (statusFilter === "reviewed" && !isReviewed(row)) {
      return false;
    }
    if (!["all", "needs_review", "reviewed"].includes(statusFilter) && resultOf(row) !== statusFilter) {
      return false;
    }
    const keyword = search.trim().toLowerCase();
    if (!keyword) {
      return true;
    }
    return [
      row.order_id,
      row.marketplace_status,
      row.tracking_numbers?.join(" "),
      row.products?.join(" "),
      row.marketplace_product_details?.map((item) => [
        item.name,
        item.variation,
        item.color,
        item.size,
        item.sku,
      ].join(" ")).join(" "),
      row.sales?.map((sale) => sale.number).join(" "),
      row.sales?.flatMap((sale) => sale.item_details || []).map((item) => [
        item.name,
        item.color,
        item.size,
        item.sku,
      ].join(" ")).join(" "),
      row.linked_flash_sale?.number,
    ].some((value) => String(value || "").toLowerCase().includes(keyword));
  }), [detail.rows, flashOnly, search, statusFilter]);
  const totalPages = Math.max(1, Math.ceil(visibleRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = visibleRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  useEffect(() => {
    setPage(1);
  }, [detail.batch?.id, flashOnly, search, statusFilter]);

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setFlashOnly(false);
  };

  const summary = detail.batch?.summary || {};
  const flashOrderCount = detail.rows.filter((row) => (
    row.match_status === "cashier_only" && row.reason_code === "flash_order"
  )).length;

  return (
    <div data-testid="marketplace-shipping-page">
      <PageHeader
        title="Crosscheck Pengiriman Marketplace"
        subtitle="Bandingkan export pengiriman marketplace dengan Sales Kasir harian"
      >
        <Button
          data-testid="shipping-export-button"
          disabled={!detail.batch}
          onClick={exportBatch}
          variant="outline"
        >
          <Download className="mr-1.5 h-4 w-4" />
          Export Hasil
        </Button>
        <Button
          data-testid="shipping-delete-button"
          disabled={!detail.batch || !canWrite || loading}
          onClick={deleteBatch}
          variant="outline"
          className="border-red-300 text-red-600 hover:bg-red-50"
        >
          <Trash2 className="mr-1.5 h-4 w-4" />
          Hapus Batch
        </Button>
        <Button
          data-testid="shipping-recheck-button"
          disabled={!detail.batch || !canWrite || loading}
          onClick={recheckBatch}
          variant="outline"
        >
          <RefreshCw className="mr-1.5 h-4 w-4" />
          Periksa Ulang
        </Button>
        <Button
          data-testid="shipping-refresh-button"
          disabled={loading}
          onClick={loadBatches}
          variant="outline"
        >
          <RotateCcw className="mr-1.5 h-4 w-4" />
          Muat Ulang
        </Button>
      </PageHeader>

      <Panel className="mb-5 p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="font-semibold text-cocoa">
              Import Shipping {marketplace === "shopee" ? "Shopee" : "TikTok Shop"}
            </div>
            <div className="text-sm text-wmuted">Pakai export pengiriman marketplace untuk tanggal crosscheck.</div>
          </div>
          <Badge className="bg-status-passed/15 text-status-passed border-0">
            Format aktif: {marketplace === "shopee" ? "Shopee" : "TikTok Shop"}
          </Badge>
        </div>
        <div className="grid gap-4 md:grid-cols-[130px_130px_140px_180px_minmax(190px,1fr)_auto] md:items-end">
          <div>
            <Label htmlFor="shipping-start">Dari Tanggal</Label>
            <Input
              data-testid="shipping-start-input"
              id="shipping-start"
              onChange={(event) => setStart(event.target.value)}
              type="date"
              value={start}
            />
          </div>
          <div>
            <Label htmlFor="shipping-end">Sampai Tanggal</Label>
            <Input
              data-testid="shipping-end-input"
              id="shipping-end"
              min={start}
              onChange={(event) => setEnd(event.target.value)}
              type="date"
              value={end}
            />
          </div>
          <div>
            <Label>Marketplace</Label>
            <Select onValueChange={setMarketplace} value={marketplace}>
              <SelectTrigger data-testid="shipping-marketplace-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="shopee">Shopee</SelectItem>
                <SelectItem value="tiktok">TikTok Shop</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Toko (Customer)</Label>
            <Select onValueChange={setStoreId} value={storeId}>
              <SelectTrigger data-testid="shipping-store-select">
                <SelectValue placeholder="Pilih toko" />
              </SelectTrigger>
              <SelectContent>
                {platformStores.length === 0 ? (
                  <SelectItem disabled value="none">Belum ada mapping toko</SelectItem>
                ) : (
                  platformStores.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.customer_name}</SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="shipping-file">File Shipping XLSX</Label>
            <Input
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              data-testid="shipping-file-input"
              id="shipping-file"
              onChange={(event) => setFile(event.target.files?.[0] || null)}
              type="file"
            />
          </div>
          <Button
            className="bg-cocoa text-ivory hover:bg-cocoa-dark"
            data-testid="shipping-upload-button"
            disabled={!canWrite || uploading}
            onClick={upload}
          >
            <Upload className="mr-1.5 h-4 w-4" />
            {uploading ? "Memproses..." : "Crosscheck"}
          </Button>
        </div>
        {canWrite && !storeId && (
          <div className="mt-3 text-sm text-status-review" data-testid="shipping-store-warning">
            Pilih Toko agar batch punya identitas jelas. Tanpa toko, batch ditandai "belum terkonfirmasi" untuk ditinjau.
          </div>
        )}
        {!canWrite && (
          <div className="mt-3 text-sm text-status-reject" data-testid="shipping-readonly-note">
            Anda dapat melihat hasil crosscheck, tetapi tidak memiliki izin impor atau review.
          </div>
        )}
      </Panel>

      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <SummaryCard label="Cocok Lengkap" value={summary.matched} tone="text-status-passed" />
        <SummaryCard label="Perlu Cek Varian" value={summary.needs_variant_check} tone="text-status-review" />
        <SummaryCard label="Beda Varian/Qty" value={(summary.variant_mismatch || 0) + (summary.qty_mismatch || 0)} tone="text-status-reject" />
        <SummaryCard label="Kasir Tanpa File" value={summary.cashier_only} tone="text-status-reject" />
        <SummaryCard label="Marketplace Tanpa Kasir" value={summary.marketplace_only} tone="text-status-review" />
        <SummaryCard label="Duplikat / Paket Ganda" value={(summary.duplicate_cashier || 0) + (summary.multiple_packages || 0)} tone="text-status-reject" />
        <SummaryCard label="Perlu Ditinjau" value={summary.needs_review} tone="text-status-review" />
      </div>

      {detail.batch?.source_date_filter_mode === "all_file_fallback" && (
        <div
          className="mb-5 flex items-start gap-2 border border-status-review/30 bg-status-review/10 p-3 text-sm text-cocoa"
          data-testid="shipping-date-fallback-notice"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-status-review" />
          Tidak ada tanggal pengiriman yang cocok dengan rentang pilihan. Seluruh Order ID pada file
          tetap dicocokkan agar transaksi tidak terlewat.
        </div>
      )}

      <Panel className="p-4">
        <div
          className="mb-2 grid grid-cols-[minmax(0,1fr)] gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(220px,1fr)_220px_190px_auto_auto]"
          data-testid="shipping-datatable-controls"
        >
          <div className="min-w-0">
            <Label>Cari Order ID / Resi / Sales</Label>
            <Input
              data-testid="shipping-search-input"
              className="min-w-0"
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Cari Order ID, resi, atau nomor Sales..."
              value={search}
            />
          </div>
          <div className="min-w-0">
            <Label>Batch Crosscheck</Label>
            <Select
              onValueChange={openBatch}
              value={detail.batch?.id || ""}
            >
              <SelectTrigger data-testid="shipping-batch-select">
                <SelectValue placeholder="Pilih batch" />
              </SelectTrigger>
              <SelectContent>
                {batches.map((batch) => (
                  <SelectItem key={batch.id} value={batch.id}>
                    {batch.marketplace.toUpperCase()} · {batch.start} · {batch.filename}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-0">
            <Label>Status</Label>
            <Select onValueChange={setStatusFilter} value={statusFilter}>
              <SelectTrigger className="min-w-0" data-testid="shipping-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                <SelectItem value="needs_review" data-testid="shipping-filter-needs-review">Perlu Ditinjau</SelectItem>
                <SelectItem value="reviewed" data-testid="shipping-filter-reviewed">Sudah Ditinjau</SelectItem>
                {Object.entries(STATUS_META).filter(([value]) => value !== "matched").map(([value, meta]) => (
                  <SelectItem key={value} value={value}>{meta.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-0 self-end">
            <Button
              aria-pressed={flashOnly}
              className={flashOnly ? "bg-cocoa text-ivory hover:bg-cocoa-dark" : ""}
              data-testid="shipping-flash-only-filter"
              onClick={() => setFlashOnly((value) => !value)}
              size="sm"
              variant={flashOnly ? "default" : "outline"}
            >
              <Tag className="mr-1.5 h-4 w-4" />
              Hanya Pesanan Kilat
            </Button>
          </div>
          <div className="min-w-0 self-end">
            <Button
              data-testid="shipping-reset-filter"
              onClick={resetFilters}
              size="icon"
              title="Reset filter"
              variant="outline"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <p className="mb-4 text-xs text-wmuted" data-testid="shipping-result-count">
          Menampilkan {pageRows.length} dari {visibleRows.length} Order ID · {flashOrderCount}
          {" "}Pesanan Kilat teridentifikasi
        </p>

        {!detail.batch ? (
          <div className="py-14 text-center text-wmuted" data-testid="shipping-empty-state">
            Unggah file shipping {marketplace === "shopee" ? "Shopee" : "TikTok Shop"} untuk membuat crosscheck harian.
          </div>
        ) : (
          <>
            <div className="hidden 2xl:block">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Order ID</TableHead>
                  <TableHead>Status Crosscheck</TableHead>
                  <TableHead>Status / Resi Marketplace</TableHead>
                  <TableHead>Detail Produk Pesanan</TableHead>
                  <TableHead>Sales Kasir</TableHead>
                  <TableHead>Rekomendasi</TableHead>
                  <TableHead className="text-right">Tindakan</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((row) => {
                  const meta = STATUS_META[resultOf(row)] || STATUS_META.matched;
                  return (
                    <TableRow data-testid={`shipping-row-${row.id}`} key={row.id}>
                      <TableCell>
                        <div className="font-mono text-sm font-semibold text-cocoa">{row.order_id}</div>
                        <div className="text-xs text-wmuted">Order {fmtDate(row.order_date)}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className={meta.className} data-testid={`shipping-status-${row.id}`}>
                          {meta.label}
                        </Badge>
                        {row.review_action && (
                          <div className="mt-1 text-xs text-wmuted">{ACTION_META[row.review_action]}</div>
                        )}
                        {(row.reason_code || row.match_status === "marketplace_only") && (
                          <div className="mt-1 text-xs font-medium text-cocoa" data-testid={`shipping-reason-${row.id}`}>
                            {REASON_META[row.reason_code] || "Order customer · Cari Pesanan Kilat"}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{row.marketplace_status}</div>
                        <div className="text-xs text-wmuted">{row.tracking_numbers?.join(" · ") || "Belum ada resi"}</div>
                      </TableCell>
                      <TableCell className="min-w-[300px]" data-testid={`shipping-products-${row.id}`}>
                        <ShippingVariantDetails row={row} />
                        {row.shipping_options?.length ? (
                          <div className="mt-2 text-xs text-wmuted">
                            {row.shipping_options.join(" · ")}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {row.sales?.length ? row.sales.map((sale) => (
                          <div className="flex items-center gap-1" data-testid={`shipping-sale-${row.id}-${sale.id}`} key={sale.id}>
                            <span className="font-medium text-cocoa">{sale.number}</span>
                            <span className="ml-1 text-xs text-wmuted">{sale.customer_name || "-"}</span>
                            {canWrite && (
                              <Button
                                className="h-6 w-6"
                                data-testid={`shipping-delete-sale-${row.id}-${sale.id}`}
                                onClick={() => deleteSale(row, sale)}
                                size="icon"
                                title="Hapus Sales ini (double / tidak sesuai)"
                                variant="ghost"
                              >
                                <Trash2 className="h-3.5 w-3.5 text-status-reject" />
                              </Button>
                            )}
                          </div>
                        )) : row.linked_flash_sale ? (
                          <div data-testid={`shipping-flash-link-${row.id}`}>
                            <span className="text-xs text-wmuted">Pesanan Kilat:</span>
                            <span className="ml-1 font-medium text-cocoa">{row.linked_flash_sale.number}</span>
                          </div>
                        ) : <span className="text-sm text-wmuted">Tidak ditemukan</span>}
                      </TableCell>
                      <TableCell className="max-w-xs text-sm text-wmuted">
                        {row.recommendation}
                        {row.review_note && <div className="mt-1 text-xs text-cocoa">{row.review_note}</div>}
                      </TableCell>
                      <TableCell className="text-right">
                        {isReviewed(row) ? (
                          <div className="flex items-center justify-end gap-1 text-status-passed" data-testid={`shipping-matched-${row.id}`}>
                            <CheckCircle2 className="h-5 w-5" />
                            {!isDone(row) && <span className="text-xs">Ditinjau</span>}
                          </div>
                        ) : (
                          <div className="flex justify-end gap-1 whitespace-nowrap">
                            {row.match_status === "marketplace_only" && (
                              <Button
                                data-testid={`shipping-link-flash-${row.id}`}
                                disabled={!canWrite}
                                onClick={() => setFlashLink(row)}
                                size="icon"
                                title="Hubungkan ke Pesanan Kilat"
                                variant="outline"
                              >
                                <Link2 className="h-4 w-4 text-cocoa" />
                              </Button>
                            )}
                            {row.match_status === "marketplace_only" && (
                              <Button
                                data-testid={`shipping-create-cashier-${row.id}`}
                                disabled={!canWrite}
                                onClick={() => createCashierFromOrder(row)}
                                size="icon"
                                title="Buat Kasir dari Order"
                                variant="outline"
                              >
                                <PlusCircle className="h-4 w-4 text-cocoa" />
                              </Button>
                            )}
                            {["variant_mismatch", "qty_mismatch"].includes(resultOf(row)) && row.sales?.length > 0 && (
                              <Button
                                data-testid={`shipping-correct-${row.id}`}
                                disabled={!canWrite}
                                onClick={() => navigate("/sales", { state: { editSaleId: row.sales[0].id } })}
                                size="icon"
                                title="Koreksi / Edit Sales ini"
                                variant="outline"
                              >
                                <Pencil className="h-4 w-4 text-cocoa" />
                              </Button>
                            )}
                            {row.match_status === "cashier_only" && row.reason_code !== "flash_order" && (
                              <Button
                                data-testid={`shipping-mark-flash-${row.id}`}
                                disabled={!canWrite}
                                onClick={() => setReview({ row, action: "mark_flash_order" })}
                                size="icon"
                                title="Tandai sebagai Pesanan Kilat"
                                variant="outline"
                              >
                                <Tag className="h-4 w-4 text-cocoa" />
                              </Button>
                            )}
                            {row.match_status === "cashier_only" && row.reason_code !== "flash_order" && (
                              <Button
                                data-testid={`shipping-outside-marketplace-${row.id}`}
                                disabled={!canWrite}
                                onClick={() => setReview({ row, action: "outside_marketplace" })}
                                size="icon"
                                title="Tandai Penjualan Luar Marketplace"
                                variant="outline"
                              >
                                <ExternalLink className="h-4 w-4 text-cocoa" />
                              </Button>
                            )}
                            <Button
                              data-testid={`shipping-resolve-${row.id}`}
                              disabled={!canWrite}
                              onClick={() => setReview({ row, action: "resolved" })}
                              size="icon"
                              title="Selesai ditinjau"
                              variant="outline"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                            <Button
                              data-testid={`shipping-retry-${row.id}`}
                              disabled={!canWrite}
                              onClick={() => setReview({ row, action: "retry_shipping" })}
                              size="icon"
                              title="Rekomendasi kirim ulang"
                              variant="outline"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                            <Button
                              data-testid={`shipping-cancel-recommendation-${row.id}`}
                              disabled={!canWrite}
                              onClick={() => setReview({ row, action: "recommend_cancel" })}
                              size="icon"
                              title="Rekomendasi pembatalan"
                              variant="outline"
                            >
                              <XCircle className="h-4 w-4 text-status-reject" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!visibleRows.length && (
                  <TableRow>
                    <TableCell className="py-10 text-center text-wmuted" colSpan={7} data-testid="shipping-no-results">
                      Tidak ada hasil untuk filter ini.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </div>
            <div className="divide-y divide-taupe/20 2xl:hidden" data-testid="shipping-mobile-list">
            {pageRows.map((row) => {
              const meta = STATUS_META[resultOf(row)] || STATUS_META.matched;
              return (
                <div className="space-y-3 py-4" data-testid={`shipping-mobile-row-${row.id}`} key={row.id}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-mono text-sm font-semibold text-cocoa">{row.order_id}</div>
                      <div className="text-xs text-wmuted">Order {fmtDate(row.order_date)}</div>
                    </div>
                    <Badge className={meta.className} data-testid={`shipping-mobile-status-${row.id}`}>
                      {meta.label}
                    </Badge>
                  </div>
                  <div className="grid gap-2 text-sm sm:grid-cols-2">
                    <div>
                      <div className="text-xs text-wmuted">Marketplace / Resi</div>
                      <div className="text-cocoa">{row.marketplace_status}</div>
                      <div className="text-xs text-wmuted">
                        {row.tracking_numbers?.join(" · ") || "Belum ada resi"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-wmuted">Sales Kasir</div>
                      {row.sales?.length ? row.sales.map((sale) => (
                        <div className="flex items-center gap-1 text-sm text-cocoa" key={sale.id}>
                          <span>{sale.number} · {sale.customer_name || "-"}</span>
                          {canWrite && (
                            <Button
                              className="h-6 w-6"
                              data-testid={`shipping-mobile-delete-sale-${row.id}-${sale.id}`}
                              onClick={() => deleteSale(row, sale)}
                              size="icon"
                              title="Hapus Sales ini"
                              variant="ghost"
                            >
                              <Trash2 className="h-3.5 w-3.5 text-status-reject" />
                            </Button>
                          )}
                        </div>
                      )) : row.linked_flash_sale ? (
                        <div className="text-sm text-cocoa">{row.linked_flash_sale.number}</div>
                      ) : (
                        <div className="text-sm text-wmuted">Tidak ditemukan</div>
                      )}
                    </div>
                  </div>
                  <div data-testid={`shipping-mobile-products-${row.id}`}>
                    <div className="mb-1 text-xs text-wmuted">Detail Produk & Varian</div>
                    <ShippingVariantDetails
                      idPrefix="shipping-mobile-variant-details"
                      row={row}
                    />
                    {row.shipping_options?.length ? (
                      <div className="mt-2 text-xs text-wmuted">
                        {row.shipping_options.join(" · ")}
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <div className="text-xs text-wmuted">Rekomendasi</div>
                    <div className="text-sm text-cocoa">{row.recommendation}</div>
                    {row.review_note && <div className="mt-1 text-xs text-wmuted">{row.review_note}</div>}
                  </div>
                  <MobileShippingActions
                    canWrite={canWrite}
                    onMarkFlash={(row) => setReview({ row, action: "mark_flash_order" })}
                    onFlashLink={setFlashLink}
                    onReview={setReview}
                    row={row}
                  />
                </div>
              );
            })}
            {!visibleRows.length && (
              <div className="py-10 text-center text-wmuted" data-testid="shipping-mobile-no-results">
                Tidak ada hasil untuk filter ini.
              </div>
            )}
            </div>
          </>
        )}
        {detail.batch && visibleRows.length > 0 && (
          <div
            className="mt-4 flex flex-wrap items-center justify-between gap-3"
            data-testid="shipping-pagination"
          >
            <p className="text-xs text-wmuted" data-testid="shipping-pagination-info">
              Halaman {currentPage} dari {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                data-testid="shipping-previous-page"
                disabled={currentPage <= 1}
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                size="sm"
                variant="outline"
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Sebelumnya
              </Button>
              <Button
                data-testid="shipping-next-page"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                size="sm"
                variant="outline"
              >
                Berikutnya
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Panel>

      {review && (
        <ReviewDialog
          batchId={detail.batch.id}
          onClose={() => setReview(null)}
          onSaved={async () => {
            setReview(null);
            await openBatch(detail.batch.id);
            await loadBatches();
          }}
          review={review}
        />
      )}
      {flashLink && (
        <FlashOrderLinkDialog
          batchId={detail.batch.id}
          onClose={() => setFlashLink(null)}
          onSaved={async () => {
            setFlashLink(null);
            await openBatch(detail.batch.id);
          }}
          row={flashLink}
        />
      )}
    </div>
  );
}

function MobileShippingActions({ canWrite, onFlashLink, onMarkFlash, onReview, row }) {
  if (isReviewed(row)) {
    return (
      <div className="flex items-center gap-2 text-sm text-status-passed">
        <CheckCircle2 className="h-5 w-5" />
        {isDone(row)
          ? (row.result_status === "verified_exception" ? "Pengecualian terverifikasi" : "Cocok lengkap")
          : "Sudah ditinjau"}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2" data-testid={`shipping-mobile-actions-${row.id}`}>
      {row.match_status === "marketplace_only" && (
        <Button
          data-testid={`shipping-mobile-link-flash-${row.id}`}
          disabled={!canWrite}
          onClick={() => onFlashLink(row)}
          size="sm"
          variant="outline"
        >
          <Link2 className="mr-1.5 h-4 w-4" />
          Hubungkan Pesanan Kilat
        </Button>
      )}
      {row.match_status === "cashier_only" && row.reason_code !== "flash_order" && (
        <Button
          data-testid={`shipping-mobile-mark-flash-${row.id}`}
          disabled={!canWrite}
          onClick={() => onMarkFlash(row)}
          size="sm"
          variant="outline"
        >
          <Tag className="mr-1.5 h-4 w-4" />
          Tandai Pesanan Kilat
        </Button>
      )}
      {row.match_status === "cashier_only" && row.reason_code !== "flash_order" && (
        <Button
          data-testid={`shipping-mobile-outside-marketplace-${row.id}`}
          disabled={!canWrite}
          onClick={() => onReview({ row, action: "outside_marketplace" })}
          size="sm"
          variant="outline"
        >
          <ExternalLink className="mr-1.5 h-4 w-4" />
          Luar Marketplace
        </Button>
      )}
      <Button
        data-testid={`shipping-mobile-resolve-${row.id}`}
        disabled={!canWrite}
        onClick={() => onReview({ row, action: "resolved" })}
        size="sm"
        variant="outline"
      >
        <CheckCircle2 className="mr-1.5 h-4 w-4" />
        Selesai Ditinjau
      </Button>
      <Button
        data-testid={`shipping-mobile-retry-${row.id}`}
        disabled={!canWrite}
        onClick={() => onReview({ row, action: "retry_shipping" })}
        size="sm"
        variant="outline"
      >
        <RotateCcw className="mr-1.5 h-4 w-4" />
        Kirim Ulang
      </Button>
      <Button
        data-testid={`shipping-mobile-cancel-recommendation-${row.id}`}
        disabled={!canWrite}
        onClick={() => onReview({ row, action: "recommend_cancel" })}
        size="sm"
        variant="outline"
      >
        <XCircle className="mr-1.5 h-4 w-4 text-status-reject" />
        Rekomendasi Batal
      </Button>
    </div>
  );
}

function FlashOrderLinkDialog({ batchId, onClose, onSaved, row }) {
  const [candidates, setCandidates] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/marketplace-shipping/batches/${batchId}/rows/${row.id}/flash-candidates`)
      .then((response) => setCandidates(response.data.candidates || []))
      .catch((error) => toast.error(formatApiError(error.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [batchId, row.id]);

  const save = async () => {
    if (!selectedId) {
      toast.error("Pilih Sales Pesanan Kilat terlebih dahulu");
      return;
    }
    setSaving(true);
    try {
      await api.put(
        `/marketplace-shipping/batches/${batchId}/rows/${row.id}/link-flash-order`,
        { flash_sale_id: selectedId, note },
      );
      toast.success("Order customer terhubung ke Pesanan Kilat");
      await onSaved();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        aria-describedby="flash-link-description"
        className="max-w-3xl"
        data-testid="shipping-flash-link-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-cocoa">Hubungkan ke Pesanan Kilat</DialogTitle>
          <DialogDescription id="flash-link-description">
            Pilih Sales Pesanan Kilat sebelumnya dengan produk paling sesuai. Order customer tetap
            berstatus Perlu Ditinjau sampai Accounting menyelesaikan review.
          </DialogDescription>
        </DialogHeader>
        <div className="border-y border-taupe/20 py-2 text-sm" data-testid="shipping-flash-source">
          <span className="text-wmuted">Order customer:</span>
          <span className="ml-1 font-mono font-semibold text-cocoa">{row.order_id}</span>
          <span className="ml-3 text-wmuted">Produk:</span>
          <span className="ml-1 text-cocoa">{row.products?.join(" · ") || "-"}</span>
        </div>
        <div className="max-h-72 overflow-y-auto border border-taupe/20">
          {loading && <div className="p-6 text-center text-sm text-wmuted" data-testid="shipping-flash-candidates-loading">Memuat Pesanan Kilat...</div>}
          {!loading && !candidates.length && <div className="p-6 text-center text-sm text-wmuted" data-testid="shipping-flash-candidates-empty">Belum ditemukan Sales Pesanan Kilat yang dapat dipilih.</div>}
          {!loading && candidates.map((candidate) => (
            <button
              className={`flex w-full items-start justify-between gap-3 border-b border-taupe/15 p-3 text-left last:border-0 ${selectedId === candidate.id ? "bg-cocoa/10" : "hover:bg-ivory"}`}
              data-testid={`shipping-flash-candidate-${candidate.id}`}
              key={candidate.id}
              onClick={() => setSelectedId(candidate.id)}
              type="button"
            >
              <div>
                <div className="font-mono text-sm font-semibold text-cocoa">{candidate.number}</div>
                <div className="text-xs text-wmuted">{candidate.order_id} · {fmtDate(candidate.created_at)}</div>
                <div className="mt-1 text-sm text-cocoa">{candidate.products?.join(" · ") || "Produk tidak tercatat"}</div>
              </div>
              <Badge className={candidate.product_match_score ? "bg-status-passed/15 text-status-passed border-0" : "bg-taupe/20 text-wmuted border-0"}>
                {candidate.product_match_score ? `${candidate.product_match_score} produk cocok` : "Periksa manual"}
              </Badge>
            </button>
          ))}
        </div>
        <div>
          <Label htmlFor="flash-link-note">Catatan Accounting</Label>
          <Textarea
            data-testid="shipping-flash-link-note"
            id="flash-link-note"
            onChange={(event) => setNote(event.target.value)}
            placeholder="Contoh: Customer mengambil stok dari Pesanan Kilat yang dipilih."
            value={note}
          />
        </div>
        <DialogFooter>
          <Button data-testid="shipping-flash-link-cancel" onClick={onClose} variant="outline">Batal</Button>
          <Button
            className="bg-cocoa text-ivory hover:bg-cocoa-dark"
            data-testid="shipping-flash-link-save"
            disabled={!selectedId || saving}
            onClick={save}
          >
            {saving ? "Menyimpan..." : "Simpan Hubungan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewDialog({ batchId, onClose, onSaved, review }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const actionLabel = ACTION_META[review.action];

  const save = async () => {
    setSaving(true);
    try {
      await api.put(
        `/marketplace-shipping/batches/${batchId}/rows/${review.row.id}/review`,
        { action: review.action, note },
      );
      toast.success("Tindakan review disimpan");
      await onSaved();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent aria-describedby="shipping-review-description" data-testid="shipping-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-cocoa">{actionLabel}</DialogTitle>
          <DialogDescription id="shipping-review-description">
            {review.action === "mark_flash_order"
              ? `Order ${review.row.order_id} akan ditandai sebagai Pesanan Kilat dan menunggu `
                + "order customer dari marketplace."
              : `Order ${review.row.order_id}. Tindakan ini hanya mencatat rekomendasi, tanpa `
                + "membatalkan Sales otomatis."}
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="shipping-review-note">Catatan Accounting</Label>
          <Textarea
            data-testid="shipping-review-note"
            id="shipping-review-note"
            onChange={(event) => setNote(event.target.value)}
            placeholder="Contoh: Resi gagal scan, ajukan kirim ulang hari ini."
            value={note}
          />
        </div>
        <DialogFooter>
          <Button data-testid="shipping-review-cancel" onClick={onClose} variant="outline">Batal</Button>
          <Button
            className="bg-cocoa text-ivory hover:bg-cocoa-dark"
            data-testid="shipping-review-save"
            disabled={saving}
            onClick={save}
          >
            {saving ? "Menyimpan..." : "Simpan Tindakan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}